/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { CustomModal } from './google-map-modal';
import { AddressListModal } from './address-list-modal';
import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';

export interface ILocationSelectorProps {
    isModalOpen: any;
    toggleMapModal: () => void;
    setSelectedMarker: (marker: any) => void;
    setAddressBarDetails: (data: string) => void;
    mapApiKey: string;
    selectedMarker: any;
    address: any;
    addressPurposes: any;
    addressBarDetails: string;
    setActiveModalContent: (data: string) => void;
    activeModalContent: string;
    selectedAddress: any;
    setSelectedAddress: (data: any) => void;
    isEligibleForQD: boolean;
    isEligibleForQuickDelivery: (lat: number, lng: number) => any;
    setAddressLatLng: (data: any) => void;
    cart?: ICartState | undefined;
    userToken?: string;
    polygonsData: any[];
}

/**
 *
 * LocationSelector component.
 * @extends {React.PureComponent<ILocationSelectorProps>}
 */

@observer
export class LocationSelector extends React.PureComponent<ILocationSelectorProps> {
    constructor(props: ILocationSelectorProps) {
        super(props);
    }

    public componentDidMount() {
        // Show current location pin details
        if (this.props.selectedMarker) {
            this.props.setAddressLatLng(JSON.stringify({ lat: this.props.selectedMarker.lat, lng: this.props.selectedMarker.lng }));
            if (this.props.selectedMarker?.name) {
                this.props.setAddressBarDetails(this.props.selectedMarker.name);
            } else if (this.props.selectedMarker?.address_name) {
                this.props.setAddressBarDetails(this.props.selectedMarker.address_name);
            }
        }

        if (this.props.address && this.props.address.length > 0) {
            this.props.setActiveModalContent('address');
        }
        // Show Nearest address location pin details if no saved address in LS & if no location pin selected by user
        if (
            this.props.address &&
            this.props.address.length > 0 &&
            this.props.selectedAddress === 0 &&
            this.props.addressBarDetails !== 'Select location'
        ) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        // Calculate the distance between the user and each address
                        const sortedAddressObject = this.props.address.map((addr: any) => {
                            const latLng = addr.DistrictName.split(',');
                            const dist = this.fetchDistance(latLng[0], latLng[1], position.coords.latitude, position.coords.longitude);
                            return { distance: dist, ...addr };
                        });

                        // Sort by distance nearest address
                        sortedAddressObject.sort(
                            (a: { distance: string }, b: { distance: string }) => parseFloat(a.distance) - parseFloat(b.distance)
                        );

                        // console.log('thiss Distance (km): ', sortedAddressObject);
                        if (sortedAddressObject && sortedAddressObject[0]) {
                            const addressType = this.props.addressPurposes.filter(
                                (addressTypeValue: { AddressType: any }) =>
                                    addressTypeValue.AddressType === sortedAddressObject[0].AddressTypeValue
                            );
                            const addressTypeValue = addressType.length > 0 ? addressType[0].Description : '';
                            this.props.setActiveModalContent('address');
                            const latLngtObject = sortedAddressObject[0].DistrictName && sortedAddressObject[0].DistrictName.split(',');
                            const latitude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[0]) : 0;
                            const longitutude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[1]) : 0;
                            this.props.setAddressLatLng(JSON.stringify({ lat: latitude, lng: longitutude }));
                            this.props.setAddressBarDetails(`${addressTypeValue} - ${sortedAddressObject[0].Street}`);
                            this.props.setSelectedAddress(sortedAddressObject[0].RecordId);
                        }
                    },
                    error => {
                        console.error('Error getting user location:', error);
                    }
                );
            }
        }
        // Show selected address from LS here
        if (this.props.selectedAddress !== 0) {
            const selectAddDetails = this.props.address.filter((item: { RecordId: any }) => item.RecordId === this.props.selectedAddress);
            if (selectAddDetails && selectAddDetails.length > 0) {
                const addressType = this.props.addressPurposes.filter(
                    (addressTypeValue: { AddressType: any }) => addressTypeValue.AddressType === selectAddDetails[0].AddressTypeValue
                );
                const addressTypeValue = addressType.length > 0 ? addressType[0].Description : '';
                this.props.setAddressBarDetails(`${addressTypeValue} - ${selectAddDetails[0].Street}`);
                const latLngtObject = selectAddDetails[0].DistrictName && selectAddDetails[0].DistrictName.split(',');
                const latitude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[0]) : 0;
                const longitutude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[1]) : 0;
                this.props.setAddressLatLng(JSON.stringify({ lat: latitude, lng: longitutude }));
                this.props.setActiveModalContent('address');
            }
        }
    }

    // Haversine formula in JavaScript
    public fetchDistance(lat: number, lon: number, currentLat: number, currentLng: number) {
        const R = 6371; // Radius of the earth in km
        const dLat = (lat - currentLat) * (Math.PI / 180);
        const dLon = (lon - currentLng) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((currentLat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }
    ///
    public render(): JSX.Element {
        return (
            <>
                <Button className='ms-location-selector' onClick={this.props.toggleMapModal}>
                    <p className='ms-wishlist-text-new'>Deliver here</p>
                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M11.5 5C11.5 7.49 7 13.5 7 13.5C7 13.5 2.5 7.49 2.5 5C2.5 3.80653 2.97411 2.66193 3.81802 1.81802C4.66193 0.974106 5.80653 0.5 7 0.5C8.19347 0.5 9.33807 0.974106 10.182 1.81802C11.0259 2.66193 11.5 3.80653 11.5 5V5Z'
                            stroke='black'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                        <path
                            d='M7 6.5C7.82843 6.5 8.5 5.82843 8.5 5C8.5 4.17157 7.82843 3.5 7 3.5C6.17157 3.5 5.5 4.17157 5.5 5C5.5 5.82843 6.17157 6.5 7 6.5Z'
                            stroke='black'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>

                    <span className=''>{`${this.props.addressBarDetails.substring(0, 20)}...`}</span>
                    <img src='https://files-eu-prod.cms.commerce.dynamics.com/cms/api/vnnhvggtsx/binary/MD8qC' className='drop-arrow'></img>
                </Button>

                {this.props.isEligibleForQD && (
                    <Button className='quick-delivery-header'>
                        <p className='delivery-text'>Get delivery in</p>
                        <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <g clipPath='url(#clip0_4072_4691)'>
                                <path
                                    d='M7 13.5C10.0376 13.5 12.5 11.0376 12.5 8C12.5 4.96243 10.0376 2.5 7 2.5C3.96243 2.5 1.5 4.96243 1.5 8C1.5 11.0376 3.96243 13.5 7 13.5Z'
                                    stroke='#000001'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path d='M5.5 0.5H8.5' stroke='#000001' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M7 0.5V2.5' stroke='#000001' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M12 2L13 3' stroke='#000001' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M2 2L1 3' stroke='#000001' strokeLinecap='round' strokeLinejoin='round' />
                            </g>
                            <path
                                d='M9.5102 2.03288L5.39568 5.43704C5.28596 5.52773 5.19868 5.64254 5.14064 5.77251C5.08261 5.90249 5.05538 6.04412 5.0611 6.18635C5.06682 6.32858 5.10532 6.46756 5.1736 6.59246C5.24188 6.71736 5.33809 6.8248 5.45473 6.90639L6.24151 7.45522C6.44113 7.59447 6.57761 7.80697 6.62121 8.04642C6.6648 8.28587 6.61198 8.53285 6.47424 8.73351L3.02493 13.7703C3.00486 13.8003 2.99648 13.8366 3.00136 13.8723C3.00624 13.9081 3.02404 13.9408 3.05141 13.9643C3.07878 13.9879 3.11381 14.0005 3.1499 14C3.18598 13.9994 3.22061 13.9857 3.24724 13.9613L9.10899 8.38441C9.20747 8.29052 9.28399 8.17603 9.33309 8.04914C9.38219 7.92224 9.40266 7.78607 9.39304 7.65035C9.38342 7.51462 9.34394 7.3827 9.27742 7.264C9.21091 7.1453 9.119 7.04276 9.00826 6.9637L8.08427 6.30371C7.88882 6.16444 7.75536 5.95446 7.71223 5.71838C7.6691 5.48229 7.7197 5.23869 7.85328 5.03931L9.7273 2.2274C9.74626 2.19788 9.75408 2.16258 9.74937 2.12781C9.74466 2.09304 9.72773 2.06109 9.7016 2.03768C9.67547 2.01427 9.64185 2.00093 9.60678 2.00005C9.57171 1.99917 9.53747 2.01081 9.5102 2.03288Z'
                                fill='black'
                            />
                            <defs>
                                <clipPath id='clip0_4072_4691'>
                                    <rect width='14' height='14' fill='white' />
                                </clipPath>
                            </defs>
                        </svg>
                        <span className='quick-delivery-timing'>60 mins.</span>
                    </Button>
                )}

                {this.props.isModalOpen && this.props.activeModalContent === 'location' && (
                    <CustomModal
                        isOpen={this.props.isModalOpen}
                        onRequestClose={this.props.toggleMapModal}
                        setSelectedMarker={this.props.setSelectedMarker}
                        mapApiKey={this.props.mapApiKey}
                        setActiveModalContent={this.props.setActiveModalContent}
                        activeModalContent={this.props.activeModalContent}
                        setAddressBarDetails={this.props.setAddressBarDetails}
                        address={this.props.address}
                        polygonsData={this.props.polygonsData}
                    />
                )}
                {this.props.isModalOpen && this.props.activeModalContent === 'address' && (
                    <AddressListModal
                        userToken={this.props.userToken}
                        cart={this.props.cart}
                        isOpen={this.props.isModalOpen}
                        onRequestClose={this.props.toggleMapModal}
                        addressData={this.props.address}
                        setActiveModalContent={this.props.setActiveModalContent}
                        activeModalContent={this.props.activeModalContent}
                        addressPurposes={this.props.addressPurposes}
                        selectedAddress={this.props.selectedAddress}
                        setSelectedAddress={this.props.setSelectedAddress}
                        isEligibleForQuickDelivery={this.props.isEligibleForQuickDelivery}
                        setAddressBarDetails={this.props.setAddressBarDetails}
                        setAddressLatLng={this.props.setAddressLatLng}
                        isEligibleForQD={this.props.isEligibleForQD}
                    />
                )}
            </>
        );
    }
}
